<script setup lang="ts">
import { I18nKey } from '@/utils/typings.ts';
import { ZxcvbnResult } from '@zxcvbn-ts/core';
import { upperFirst } from 'lodash';
import { DeepReadonly, UnwrapNestedRefs } from 'vue';

type PasswordQualityEstimationProps = {
  result: DeepReadonly<UnwrapNestedRefs<ZxcvbnResult>>;
};
defineProps<PasswordQualityEstimationProps>();

const scoreMax = 4;
const numGaps = scoreMax - 1;
const gapWidth = 1;
const width = (100 - gapWidth * numGaps) / scoreMax;
const height = 1;
const strokeWidth = 0.3;
</script>

<template>
  <div v-if="result.password.length">
    <svg class="password-quality-estimation" :viewBox="`0 0 100 ${height}`">
      <rect
        v-for="i in scoreMax"
        :key="i"
        :class="result.score >= i ? 'score' : 'no-score'"
        :x="(width + gapWidth) * (i - 1)"
        y="0"
        :width="width"
        :height="height"
      />
    </svg>

    <v-alert
      v-if="result.feedback.warning"
      class="warning mt-2"
      type="warning"
      :icon="false"
      variant="tonal"
    >
      {{ $t(`Zxcvbn.Warnings.${upperFirst(result.feedback.warning as string)}` as I18nKey) }}
    </v-alert>
    <v-alert
      v-if="result.feedback.suggestions.length"
      class="mt-2"
      type="info"
      variant="tonal"
      :icon="false"
    >
      <ul class="suggestions">
        <li v-for="(suggestion, j) of result.feedback.suggestions" :key="`suggestion-${j}`">
          {{ $t(`Zxcvbn.Suggestions.${upperFirst(suggestion as string)}` as I18nKey) }}
        </li>
      </ul>
    </v-alert>
  </div>
</template>

<style lang="scss" scoped>
.password-quality-estimation {
  & > rect {
    transition: fill 0.3s ease-in-out;
    stroke: rgb(var(--v-theme-secondary));
    stroke-width: v-bind(strokeWidth);
    fill: transparent;
    &.score {
      fill: rgb(var(--v-theme-secondary));
    }
  }
}
.warning {
  color: rgb(var(--v-theme-warning));
  text-align: left !important;
}
ul.suggestions {
  margin-inline-start: 1rem;
  li {
    padding-left: 0.5rem;
    text-align: left;
  }
}
</style>
